'use client'

import { Check, Link2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { track } from '@/lib/analytics'
import type { FuelType, VehicleId, Waypoint } from '@/lib/types'
import { encodeRouteToUrl } from '@/lib/url-state'
import { useRouteStore } from '@/stores/route-store'

interface ShareButtonProps {
	origin: Waypoint | null
	destination: Waypoint | null
	waypoints: Waypoint[]
	vehicle: VehicleId
	fuelType: FuelType
	/** Custom MPG override — included in share link only when set */
	customMpg: number | null
}

export function ShareButton({
	origin,
	destination,
	waypoints,
	vehicle,
	fuelType,
	customMpg,
}: ShareButtonProps) {
	// Vehicle settings travel with the link so the recipient sees the same numbers.
	const customHeightM = useRouteStore((s) => s.customHeightM)
	const auxPowerOn = useRouteStore((s) => s.auxPowerOn)
	const customGph = useRouteStore((s) => s.customGph)
	const auxIdleHours = useRouteStore((s) => s.auxIdleHours)
	const [copied, setCopied] = useState(false)

	const handleShare = async () => {
		if (!origin || !destination) {
			toast.error('Set origin and destination first')
			return
		}

		const params = encodeRouteToUrl({
			from: origin,
			to: destination,
			waypoints,
			vehicle,
			fuelType,
			customMpg,
			customHeightM,
			auxPowerOn,
			customGph,
			auxIdleHours,
		})

		const url = `${window.location.origin}?${params}`

		try {
			await navigator.clipboard.writeText(url)
			setCopied(true)
			toast.success('Link copied to clipboard')
			track('Shared', {
				vehicle,
				fuel_type: fuelType,
				waypoint_count: waypoints.length,
				has_custom_mpg: customMpg !== null,
			})
			setTimeout(() => setCopied(false), 2000)
		} catch {
			toast.error('Failed to copy link')
		}
	}

	return (
		<Button variant="outline" size="sm" onClick={handleShare} disabled={!origin || !destination}>
			{copied ? <Check className="size-3.5" /> : <Link2 className="size-3.5" />}
			{copied ? 'Copied' : 'Share'}
		</Button>
	)
}
